/**
 * Cartesia Real-Time TTS Service (Sonic-3.5)
 * Emits raw headerless PCM16 audio @ 24kHz for the Anam AI avatar.
 *
 * Transport: a single long-lived WebSocket using Cartesia "continuations".
 * One assistant turn == one context_id. Phrases are pushed into that context
 * with `continue: true`, so the model carries prosody, breath and intonation
 * across them and the turn is voiced as ONE utterance rather than a series of
 * unrelated clips.
 *
 * This replaced a request-per-phrase HTTP design, which was the cause of the
 * stutter: every phrase paid a fresh connection + time-to-first-byte, and —
 * worse — each phrase was synthesized in isolation, so pitch and pace reset at
 * every sentence boundary. That reset is what reads as "robotic".
 *
 *   push()   -> { transcript, continue: true,  context_id }
 *   end()    -> { transcript: "", continue: false, context_id }   (flush)
 *   cancel() -> { context_id, cancel: true }                      (barge-in)
 *
 * The HTTP endpoint is kept as an automatic fallback: if the socket cannot be
 * opened (auth, version, network) or dies mid-turn, remaining phrases are
 * synthesized over HTTP so Ava never goes silent.
 */

const CARTESIA_API_KEY =
  process.env.VUE_APP_CARTESIA_API_KEY || "sk_car_uxPKxwXbxbhKriVdGQ2VPZ";
// Short-lived, minted server side. Preferred in the browser — see the note in
// the README/handover: the raw API key above is readable by any visitor.
const CARTESIA_ACCESS_TOKEN = process.env.VUE_APP_CARTESIA_ACCESS_TOKEN || null;
const CARTESIA_MODEL_ID = "sonic-3.5";
const CARTESIA_VOICE_ID = "263b9cc0-0d99-44e7-ae92-3d4ad5d2ad18";
const CARTESIA_ENDPOINT = "https://api.cartesia.ai/tts/bytes";
const CARTESIA_WS_ENDPOINT = "wss://api.cartesia.ai/tts/websocket";
const CARTESIA_VERSION = "2026-03-01";
const SAMPLE_RATE = 24000;

// How long Cartesia may hold text waiting for more context before it commits
// to a prosody contour. The 3000ms default is tuned for token-by-token input;
// we push whole phrases, so a short delay is enough to let it look ahead one
// clause without adding audible lag.
const MAX_BUFFER_DELAY_MS = 120;

const OUTPUT_FORMAT = {
  container: "raw",
  encoding: "pcm_s16le",
  sample_rate: SAMPLE_RATE,
};

// Anam cue vocabulary -> the emotions Sonic accepts, so the face and the voice
// are never expressing different things.
const EMOTION_TO_CARTESIA = {
  happy: "content",
  warm: "content",
  playful: "content",
  laughter: "content",
  curious: "neutral",
  supportive: "calm",
  concerned: "calm",
  sad: "sad",
  surprised: "neutral",
  angry: "angry",
  distressed: "sad",
  neutral: "neutral",
};

let activeStream = null;

// -- shared socket ----------------------------------------------------------
let socket = null;
let socketReady = null; // Promise<WebSocket|null> while connecting
let wsDisabled = false; // set once the socket has proven unusable
const contexts = new Map(); // context_id -> stream

function buildSocketUrl() {
  const params = new URLSearchParams({ cartesia_version: CARTESIA_VERSION });
  // Browsers cannot set headers on a WebSocket handshake, so credentials go in
  // the query string. access_token is the supported browser credential.
  if (CARTESIA_ACCESS_TOKEN) params.set("access_token", CARTESIA_ACCESS_TOKEN);
  else params.set("api_key", CARTESIA_API_KEY);
  return `${CARTESIA_WS_ENDPOINT}?${params.toString()}`;
}

function handleSocketMessage(ev) {
  let msg;
  try {
    msg = JSON.parse(ev.data);
  } catch (_) {
    return;
  }
  // A context we have abandoned (barge-in) is no longer in the map, so its
  // late chunks are dropped right here — before they can reach the avatar.
  const stream = msg.context_id ? contexts.get(msg.context_id) : null;
  if (!stream) return;
  stream._onSocketMessage(msg);
}

function teardownSocket(reason) {
  const dying = socket;
  socket = null;
  socketReady = null;
  if (dying) {
    try {
      dying.onmessage = null;
      dying.onerror = null;
      dying.onclose = null;
      dying.close();
    } catch (_) {}
  }
  const orphaned = Array.from(contexts.values());
  contexts.clear();
  orphaned.forEach((s) => {
    try {
      s._onSocketLost(reason);
    } catch (_) {}
  });
}

/**
 * Resolve to an open socket, or null if the WebSocket transport is unusable
 * (in which case callers fall back to HTTP).
 */
function ensureSocket() {
  if (wsDisabled) return Promise.resolve(null);
  if (socket && socket.readyState === WebSocket.OPEN) {
    return Promise.resolve(socket);
  }
  if (socketReady) return socketReady;

  socketReady = new Promise((resolve) => {
    let ws;
    try {
      ws = new WebSocket(buildSocketUrl());
    } catch (e) {
      console.warn("[cartesia] websocket construction failed:", e);
      wsDisabled = true;
      socketReady = null;
      resolve(null);
      return;
    }
    let settled = false;
    const fail = (why) => {
      if (settled) return;
      settled = true;
      console.warn(`[cartesia] websocket unavailable (${why}); using HTTP`);
      // One failed connect is enough to stop paying the timeout on every turn.
      wsDisabled = true;
      socketReady = null;
      socket = null;
      resolve(null);
    };
    const timer = setTimeout(() => fail("timeout"), 4000);

    ws.onopen = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      socket = ws;
      socketReady = null;
      ws.onmessage = handleSocketMessage;
      ws.onerror = () => teardownSocket("error");
      ws.onclose = () => {
        // An idle socket being closed by the server is normal; the next turn
        // reopens it. Only a failure to CONNECT disables the transport.
        if (socket === ws) teardownSocket("closed");
      };
      resolve(ws);
    };
    ws.onerror = () => {
      clearTimeout(timer);
      fail("error");
    };
    ws.onclose = () => {
      clearTimeout(timer);
      fail("closed before open");
    };
  });
  return socketReady;
}

/** Open the socket ahead of the first utterance so it costs nothing later. */
export function warmCartesia() {
  ensureSocket().catch(() => {});
}

function uint8ToBase64(uint8) {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < uint8.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      uint8.subarray(i, Math.min(i + CHUNK, uint8.length)),
    );
  }
  return btoa(binary);
}

let contextCounter = 0;
function nextContextId() {
  contextCounter += 1;
  return `ava-${Date.now().toString(36)}-${contextCounter}`;
}

/**
 * Create an ordered TTS stream for one assistant turn.
 *
 * @param {object} opts
 * @param {function(string)} opts.onAudioChunk base64 PCM16 chunk
 * @param {function()}       [opts.onStart]    first chunk of the turn is out
 * @param {function()}       [opts.onDone]     turn fully synthesized
 * @param {function()}       [opts.onCancel]   turn abandoned (barge-in)
 * @param {function(Error)}  [opts.onError]
 */
export function createCartesiaStream({
  onAudioChunk,
  onStart,
  onDone,
  onCancel,
  onError,
} = {}) {
  // A new turn always supersedes the old one.
  cancelCartesiaTTS();

  const contextId = nextContextId();
  let cancelled = false;
  let ended = false;
  let finished = false;
  let emitted = false;
  let usingSocket = false;
  let openingSocket = false;
  let flushSent = false;

  // HTTP fallback state.
  let httpQueue = [];
  let httpRunning = false;
  let httpController = null;
  // Text pushed before we knew which transport we had.
  let pendingBeforeTransport = [];

  const emitChunk = (b64) => {
    if (cancelled || !b64) return;
    if (!emitted) {
      emitted = true;
      if (typeof onStart === "function") {
        try {
          onStart();
        } catch (e) {
          console.warn("[cartesia] onStart threw:", e);
        }
      }
    }
    if (typeof onAudioChunk === "function") onAudioChunk(b64);
  };

  const settle = () => {
    if (finished) return;
    finished = true;
    contexts.delete(contextId);
    if (activeStream === stream) activeStream = null;
    if (typeof onDone === "function") {
      try {
        onDone();
      } catch (e) {
        console.warn("[cartesia] onDone threw:", e);
      }
    }
  };

  const fail = (err) => {
    if (err && err.name === "AbortError") return;
    console.warn("[cartesia] TTS error:", err);
    if (typeof onError === "function") {
      try {
        onError(err);
      } catch (_) {}
    }
  };

  function socketSend(payload) {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify(payload));
      return true;
    } catch (e) {
      console.warn("[cartesia] socket send failed:", e);
      return false;
    }
  }

  function baseRequest(transcript, emotion) {
    const req = {
      model_id: CARTESIA_MODEL_ID,
      transcript,
      voice: { mode: "id", id: CARTESIA_VOICE_ID },
      output_format: OUTPUT_FORMAT,
      language: "en",
      context_id: contextId,
      max_buffer_delay_ms: MAX_BUFFER_DELAY_MS,
    };
    const mapped = emotion && EMOTION_TO_CARTESIA[emotion];
    if (mapped) req.generation_config = { emotion: mapped };
    return req;
  }

  // -- socket path ----------------------------------------------------------
  const stream = {
    _onSocketMessage(msg) {
      if (cancelled) return;
      if (msg.type === "chunk" && msg.data) {
        emitChunk(msg.data);
        return;
      }
      if (msg.type === "done") {
        settle();
        return;
      }
      if (msg.type === "error") {
        fail(new Error(msg.message || msg.title || "cartesia stream error"));
        // Give the rest of the turn to HTTP rather than dropping it.
        usingSocket = false;
        contexts.delete(contextId);
        if (ended) settle();
      }
    },

    /** The socket died with this turn still open — finish it over HTTP. */
    _onSocketLost() {
      if (cancelled || finished || !usingSocket) return;
      usingSocket = false;
      if (ended) settle();
    },

    push(text, options = {}) {
      if (cancelled || finished) return;
      const trimmed = String(text || "").trim();
      if (!trimmed) return;
      // A late phrase for a turn we already flushed re-opens it rather than
      // spawning a second, overlapping stream.
      ended = false;
      flushSent = false;

      // The socket is normally already warm by the time a turn starts (it is
      // opened at session start). Adopt it here — without this the stream only
      // ever took the socket path on the one turn that happened to open it,
      // and every turn after that silently degraded to HTTP.
      if (
        !usingSocket &&
        !openingSocket &&
        socket &&
        socket.readyState === WebSocket.OPEN
      ) {
        usingSocket = true;
        contexts.set(contextId, stream);
      }

      if (usingSocket) {
        const sent = socketSend({
          ...baseRequest(trimmed, options.emotion),
          continue: true,
        });
        if (sent) return;
        usingSocket = false; // fall through to HTTP
        contexts.delete(contextId);
      }

      if (openingSocket) {
        pendingBeforeTransport.push({ text: trimmed, options });
        return;
      }

      if (!socket && !wsDisabled) {
        openingSocket = true;
        pendingBeforeTransport.push({ text: trimmed, options });
        ensureSocket()
          .then((ws) => {
            openingSocket = false;
            if (cancelled || finished) return;
            const backlog = pendingBeforeTransport;
            pendingBeforeTransport = [];
            if (ws) {
              usingSocket = true;
              contexts.set(contextId, stream);
              // The socket can still die between resolving and sending. Any
              // phrase that does not make it onto the wire goes to HTTP —
              // dropping it silently would leave Ava mute for that turn.
              let live = true;
              backlog.forEach((item) => {
                if (live) {
                  live = socketSend({
                    ...baseRequest(item.text, item.options.emotion),
                    continue: true,
                  });
                }
                if (!live) httpEnqueue(item.text, item.options);
              });
              if (!live) {
                usingSocket = false;
                contexts.delete(contextId);
              }
              if (ended) stream.end();
            } else {
              backlog.forEach((item) => httpEnqueue(item.text, item.options));
              if (ended) stream.end();
            }
          })
          .catch(() => {
            openingSocket = false;
            const backlog = pendingBeforeTransport;
            pendingBeforeTransport = [];
            backlog.forEach((item) => httpEnqueue(item.text, item.options));
          });
        return;
      }

      httpEnqueue(trimmed, options);
    },

    end() {
      if (cancelled || finished) return;
      ended = true;
      if (openingSocket) return; // flushed once the transport is decided
      if (usingSocket) {
        // Guarded: end() is reached from both transcript.done and
        // response.done, and a second flush would produce a second `done`
        // (and so a second endSequence on the avatar).
        if (flushSent) return;
        flushSent = true;
        // Empty transcript with continue:false is Cartesia's flush: finish the
        // context and emit `done`.
        socketSend({ ...baseRequest("", null), continue: false });
        return;
      }
      if (!httpRunning && httpQueue.length === 0) settle();
    },

    cancel() {
      if (cancelled) return;
      cancelled = true;
      finished = true;
      httpQueue.length = 0;
      pendingBeforeTransport.length = 0;
      if (usingSocket) {
        // Stops generation server-side without tearing down the shared socket,
        // so the next turn does not pay a reconnect.
        socketSend({ context_id: contextId, cancel: true });
      }
      contexts.delete(contextId);
      if (httpController) {
        try {
          httpController.abort();
        } catch (_) {}
        httpController = null;
      }
      if (activeStream === stream) activeStream = null;
      if (typeof onCancel === "function") {
        try {
          onCancel();
        } catch (e) {
          console.warn("[cartesia] onCancel threw:", e);
        }
      }
    },

    isCancelled() {
      return cancelled;
    },

    getContextId() {
      return contextId;
    },
  };

  // -- HTTP fallback path ---------------------------------------------------
  function httpEnqueue(text, options) {
    httpQueue.push({ text, options: options || {} });
    httpDrain();
  }

  async function httpDrain() {
    if (httpRunning || cancelled) return;
    httpRunning = true;
    try {
      while (!cancelled && httpQueue.length) {
        const item = httpQueue.shift();
        await httpSynthesize(item.text, item.options);
      }
    } finally {
      httpRunning = false;
    }
    if (!cancelled && ended && httpQueue.length === 0 && !usingSocket) settle();
  }

  async function httpSynthesize(text, options) {
    httpController = new AbortController();
    const signal = httpController.signal;
    try {
      const body = baseRequest(text, options.emotion);
      delete body.context_id;
      delete body.max_buffer_delay_ms;
      const response = await fetch(CARTESIA_ENDPOINT, {
        method: "POST",
        headers: {
          "Cartesia-Version": CARTESIA_VERSION,
          "X-API-Key": CARTESIA_API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      });
      if (cancelled || signal.aborted) return;
      if (!response.ok) {
        throw new Error(
          `Cartesia API error ${response.status}: ${await response.text()}`,
        );
      }
      const reader = response.body.getReader();
      try {
        for (;;) {
          const { value, done } = await reader.read();
          // Re-checked after every await, so a cancel landing mid-read can
          // never deliver one more chunk to the avatar.
          if (done || cancelled || signal.aborted) break;
          if (value && value.byteLength > 0) emitChunk(uint8ToBase64(value));
        }
      } finally {
        try {
          await reader.cancel();
        } catch (_) {}
      }
    } catch (err) {
      fail(err);
    } finally {
      if (httpController && httpController.signal === signal) {
        httpController = null;
      }
    }
  }

  activeStream = stream;
  return stream;
}

/**
 * Stream TTS audio chunks for a complete transcript.
 * @param {string} text Transcript text to synthesize
 * @param {function(string)} onAudioChunk Callback receiving base64 PCM16 chunks
 */
export async function streamCartesiaTTS(text, onAudioChunk) {
  if (!text || !text.trim()) return;
  await new Promise((resolve) => {
    const stream = createCartesiaStream({
      onAudioChunk,
      onDone: resolve,
      onCancel: resolve,
      onError: resolve,
    });
    stream.push(text);
    stream.end();
  });
}

/** Instantly abort active Cartesia TTS streaming (barge-in / interruption). */
export function cancelCartesiaTTS() {
  if (activeStream) {
    const stream = activeStream;
    activeStream = null;
    stream.cancel();
  }
}

/** Drop the shared socket (session teardown). */
export function closeCartesiaTransport() {
  cancelCartesiaTTS();
  teardownSocket("teardown");
  // A fresh session gets to retry the socket even if the last one failed.
  wsDisabled = false;
}

export default {
  createCartesiaStream,
  streamCartesiaTTS,
  cancelCartesiaTTS,
  closeCartesiaTransport,
  warmCartesia,
};
